import { ICourse, ICourse2 } from '@et/types/Courses'
import DefaultTheme from '@styles/themes/defaultTheme'
import { PAYMENT_TYPES_THEME } from '@et/types/Enums'
import { deepPurple, neonPink } from '@styles/themes/darkThemeBeautifulLettering'
import { graphql } from 'gatsby'

const slug = 'flat-florals'
const title = 'Flat Florals in Procreate'
const highlight1 = `#B77DC3`
const mainColor = `#4D286F`
const course: ICourse2 = {
  theme: PAYMENT_TYPES_THEME.LIGHT,
  title,
  slug,
  videoId: `nfb07pgvxs`,
  keywords: [
    'flat florals in procreate', 'procreate florals', 'procreate flower tutorial', 'procreate tutorial', 'procreate flowers', 'digital flowers', 'digital florals', 'iPad florals', 'iPad flowers tutorial', 'iPad art', 'procreate course', 'how to draw flowers in procreate', 'procreate for beginners', 'flower course', 'draw flowers', 'how to draw flowers', 'digital art course', 'procreate art', 'prcoreat online flower course', 'everytuesday', 'procreate illustraion tutorials'
  ],
  price: '147',
  salePrice: '127',
  paymentPlan:{
    price: '55',
    perMonth: '3 Payments of $55/m',
    savings: '10%'
  },
  motionMail: `https://s.mmgo.io/t/Cbcs/scale_2x`,
  onSale: false,
  tagline: 'Learn how to digitally paint flat style floral artwork in Procreate',
  classDescriptor: 'Learn how to digitally paint flat style floral artwork in Procreate',
  intro: {
    imgName: 'procreate301/tablet-intro.png',
    imgAlt: 'Create custom modern lettering styles that are unique to you',
    bullets: [
      {
        title: 'Beginner Friendly',
        desc:
          'Step-by-step narration with closeups on all the details, so you feel confident in the process from start to finish.',
      },
      {
        title: 'A Versatile Process',
        desc:
          'Since every lettering style is different, this process has been fine-tuned to fit any situation. You’ll be able to achieve a realistic watercolor effect when you apply it to any style - hand lettered or typed.',
      },
      {
        title: 'Blend Options',
        desc:
          'You may want an ombré, one color, smooth transitions blend with one of your pieces and a multi-colored blend with water pockets on another. This course serves up 5 different blend options, so you’ll have plenty to choose from when you begin your next lettering piece.',
      },
      {
        title: 'Florals + Foliage Integration',
        desc:
          'Take your watercolor lettering even further by integrating florals and foliage! A bonus florals foundations module is included, so even if you’ve never painted florals before, you’ll be able to follow along and complete every project in the class.',
      },
    ],
  },
  projects: {
    beginner: [
      {
        title: 'Project 1',
        backgroundColor: `#c4d8da`,
        textColor: `#4a5457`,
        subheadColor: `#fff`,
        projectImg: `procreate301/project-1.jpg`,
        projectImgMobile: `procreate301/project-1.jpg`,
        type: '',
        desc:
          'We begin with a practice project, painting the same subject (leafy branch) in the 3 different types of messy watercolor (little mess to lots of mess). You’ll learn all the components needed and various methods introduced as the messiness becomes increasingly complex.',
      },
      {
        title: 'Project 2',
        backgroundColor: `#fcbfb2`,
        textColor: `#4a5457`,
        subheadColor: `#fcb900`,
        projectImg: `procreate301/project-2.jpg`,
        projectImgMobile: `procreate301/project-2-mobile.jpg`,
        type: '',
        desc:
          'We take what we went over in Project 1 and apply the mid level/extra messy aesthetic to a popsicle! We use an existing, traditional painting as a reference to guide us on what to look for when we transition to only having a photo reference.',
      },
      {
        title: 'Project 3',
        backgroundColor: `#ffe3df`,
        textColor: `#4a5457`,
        subheadColor: `#f3ecdf`,
        projectImg: `procreate301/project-3.jpg`,
        projectImgMobile: `procreate301/project-3-mobile.jpg`,
        type: '',
        desc: 'In our first project based on a photograph, we utilize techniques we established in the first two projects to interpret a photograph in the messy watercolor style. We focus on color and texture layering, how to avoid muddy colors and how to stack different blend modes for the most eye catching result.',
      },
      {
        title: 'Project 4',
        projectImg: `procreate501/project-4.jpg`,
        projectImgMobile: `procreate301/project-4-mobile.jpg`,
        backgroundColor: `#dee5dd`,
        textColor: `#565857`,
        subheadColor: `#c56b00`,
        type: '',
        desc:
          'In Project 4, we go over different ways to alter a reference photo’s color and we experiment with the level of mess we prefer, where to implement it and where our bleeds and blends will be the most impactful.',
      },
      {
        title: 'Project 5',
        projectImg: `procreate301/project-5.jpg`,
        projectImgMobile: `procreate301/project-5-mobile.jpg`,
        backgroundColor: `#ffd5bc`,
        textColor: `#4a5457`,
        subheadColor: `#419187`,
        type: '',
        desc:
          'In our first animal painting, we discuss where to keep extra details and where to apply our messiness based on the subject and the subject’s position. We layer on color and blend modes and place wet and dry textures with intention to captivate the viewer’s focus.',
      },
      {
        title: 'Project 6',
        projectImg: `procreate301/project-6.jpg`,
        projectImgMobile: `procreate301/project-6-mobile.jpg`,
        backgroundColor: `#a39691`,
        textColor: `#4a5457`,
        subheadColor: `#be8d87`,
        type: '',
        desc:
          'Our 6th project is all about simulating fur and allowing a viewer’s eye to move in the direction we choose. We pay close attention to small details in this project and see how subtle changes and choices can have dramatic effects on our final piece.',
      },
      {
        title: 'Project 7',
        projectImg: `procreate301/project-7.jpg`,
        projectImgMobile: `procreate301/project-7-mobile.jpg`,
        backgroundColor: `#95b7db`,
        textColor: `#4a5457`,
        subheadColor: `#f4d547`,
        type: '',
        desc:
          'We conclude our animal projects with the most complex of the course: a beta fish. We add hyper-realistic details and juxtapose them near extreme messiness and create a composition of dramatic, saturated color.',
      },
    ],
    advanced: [],
  },
  curriculum: [
    {
      title: 'Welcome',
      videos: [
        {
          title: 'What You\'ll Learn/Course Overview',
          duration: '8:13',
        },
        {
          title: 'My Tools + Accessories',
          duration: '5:23',
        },
        {
          title: 'Bonus Location/Install Instructions',
          duration: '8:22',
        },
        {
          title: 'How to Use the Brushes/Brushes Overview',
          duration: '4:30',
        },
        {
          title: '2 Procreate Gestures We\'ll Use',
          duration: '3:47',
        }
      ],
    },
    {
      title: 'Flat Style Overview',
      videos: [
        {
          title: 'What is the Flat Style: Examples',
          duration: '4:08',
        },
        {
          title: 'Flat style essentials',
          duration: '7:44',
        },
        {
          title: 'Simplification process',
          duration: '9:29',
        },
        {
          title: 'Choosing color',
          duration: '8:57',
        },
      ],
    },
    {
      title: 'Basic Florals + Foliage: Practice Exercises',
      videos: [
        {
          title: 'Style variations practice exercise: part 1',
          duration: '6:55',
        },
        {
          title: 'Style variations practice exercise: part 2',
          duration: '5:50',
        },
        {
          title: 'Foliage style variations practice exercise',
          duration: '8:40',
        },
      ],
    },
    {
      title: 'Project 1: Simple floral with foliage 3 ways',
      videos: [
        {
          title: 'Freehand composition with layer masking',
          duration: '8:20',
        },
        {
          title: 'Project 1: Style 1',
          duration: '4:24',
        },
        {
          title: 'Project 1: Style 2, part 1',
          duration: '4:22',
        },
        {
          title: 'Project 1: Style 2, part 2',
          duration: '2:33',
        },
        {
          title: 'Project 1: Style 3, part 1',
          duration: '5:59',
        },
        {
          title: 'Project 1: Style 3, part 2',
          duration: '4:23',
        },
      ],
    },
    {
      title: 'Project 2: Simple floral arrangement 2 ways',
      videos: [
        {
          title: 'Create the composition',
          duration: '10:47',
        },
        {
          title: 'Project 2: Style 1',
          duration: '9:54',
        },
        {
          title: 'Project 2: Style 2',
          duration: '9:09',
        },
      ],
    },
    {
      title: 'Complex Florals: Practice Exercises',
      videos: [
        {
          title: 'Style variations practice exercise: part 1',
          duration: '8:27',
        },
        {
          title: 'Style variations practice exercise: part 2',
          duration: '11:17',
        },
      ],
    },
    {
      title: 'Project 3: Complex floral with foliage 2 ways',
      videos: [
        {
          title: 'Freehand composition with layer masking',
          duration: '3:48',
        },
        {
          title: 'Project 3: Style 1',
          duration: '8:33',
        },
        {
          title: 'Project 3: Style 2',
          duration: '8:31',
        },
      ],
    },
    {
      title: 'Building Custom Compositions',
      videos: [
        {
          title: 'Composition Components',
          duration: '9:06',
        },
        {
          title: 'How to Create Custom Compositions',
          duration: '5:51',
        },
        {
          title: 'How to Use the Composition Stamps',
          duration: '6:51',
        },
      ],
    },
    {
      title: 'Project 4: Complex Vertical Arrangement',
      videos: [
        {
          title: 'Create the Composition: part 1',
          duration: '10:36',
        },
        {
          title: 'Create the Composition: part 2',
          duration: '9:52',
        },
        {
          title: 'Fill with Color: Hero 1',
          duration: '9:27',
        },
        {
          title: 'Fill with Color: Hero 2',
          duration: '6:58',
        },
        {
          title: 'Fill with Color: Hero 3',
          duration: '5:33',
        },
        {
          title: 'Fill with Color: Hero 4',
          duration: '5:07',
        },
        {
          title: 'Fill with Color: Supporting Florals',
          duration: '8:44',
        },
        {
          title: 'Fill with Color: Foliage, part 1',
          duration: '7:41',
        },
        {
          title: 'Fill with Color: Foliage, part 2',
          duration: '5:08',
        },
        {
          title: 'Fill with Color: Foliage, part 3',
          duration: '5:29',
        },
      ],
    },
    {
      title: 'Project 5: Complex Arrangement with Depth',
      videos: [
        {
          title: 'Create the Composition: part 1',
          duration: '9:13',
        },
        {
          title: 'Create the Composition: part 2',
          duration: '6:56',
        },
        {
          title: 'Fill with Color: Hero 1, part 1',
          duration: '9:07',
        },
        {
          title: 'Fill with Color: Hero 1, part 2',
          duration: '4:10',
        },
        {
          title: 'Fill with Color: Hero 2',
          duration: '12:30',
        },
        {
          title: 'Fill with Color: Hero 3',
          duration: '9:56',
        },
        {
          title: 'Fill with Color: Supporting Elements',
          duration: '9:07',
        },
        {
          title: 'Fill with Color: Foliage, part 1',
          duration: '7:03',
        },
        {
          title: 'Fill with Color: Foliage, part 2',
          duration: '3:38',
        },
      ],
    },
    {
      title: 'Project 6: Seamless Pattern with Transparent Background',
      videos: [
        {
          title: 'Create the First Bouquet: Structure',
          duration: '8:47',
        },
        {
          title: 'Create the First Bouquet: Color',
          duration: '12:13',
        },
        {
          title: 'Create the Second Bouquet: Structure',
          duration: '5:28',
        },
        {
          title: 'Create the Second Bouquet: Color',
          duration: '7:38',
        },
        {
          title: 'Create the Third Bouquet: Structure',
          duration: '3:44',
        },
        {
          title: 'Create the Third Bouquet: Color',
          duration: '6:09',
        },
        {
          title: 'Set up the Pattern Canvas',
          duration: '2:47',
        },
        {
          title: 'Insert First Bouquet',
          duration: '4:32',
        },
        {
          title: 'Insert Remaining Bouquets + Make Seamless',
          duration: '7:06',
        },
        {
          title: 'Add Final Details + Test the Pattern',
          duration: '9:04',
        },
      ],
    },
    {
      title: 'Bonus: Application Ideas',
      videos: [
        {
          title: 'Application 1: Text Ribbon',
          duration: '3:50',
        },
        {
          title: 'Application 2: Birthday Cake',
          duration: '8:34',
        },
        {
          title: 'Application 3: Floral Borders',
          duration: '7:16',
        },
      ],
    },
    {
      title: 'Thank you + next steps',
      videos: [
        {
          title: 'Thank You + Next Steps',
          duration: '3:05',
        },
      ],
    },
  ],
  bonuses: {
    headerImgName: `procreate301/bonus-title.png`,
    imgAlt: 'Messy Watercolors in Procreate Bonuses',
    gridType: 1,
    items: [
      {
        title: 'Bouquet Maker Brush Set',
        desc: '<p>The full brush set (150+ brushes!) are included with enrollment so you can follow along exactly. Brushes are separated into 5 different categories so finding what you need is quick and efficient.</p>',
        imgName: 'procreate701/bonuses/bonuses-1.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Extended License of the Bouquet Maker Brushes',
        desc: '<p>An extended license of the brush set <span class="bold bonusPriceSpan">($40 value)</span> is included, so you can use the brushes to create artwork you plan to sell or use for business purposes without limit.</p>',
        imgName: 'procreate701/bonuses/bonuses-2.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Bonus Applications Module',
        desc: '<p>Extend the life of your art by exporting it with transparent backgrounds. Next, learn how to integrate that artwork into 3 different applications, expanding its versatility for your own potential buyers.</p>',
        imgName: 'procreate701/bonuses/bonuses-3.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Facebook Group',
        desc: '<p>Your enrollment includes membership into our private Facebook community. Share your progress, get feedback and find support whenever you need it.</p>',
        imgName: 'procreate701/bonuses/bonuses-4.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Flat Style Cheat Sheets',
        desc: '<p>Graphic cheat sheets included, so you can pull ideas for style and elements quickly without having to backtrack.</p>',
        imgName: 'procreate701/bonuses/bonuses-5.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Color Palettes + Links',
        desc: '<p>Every practice exercise and project’s color palette is included, as well as helpful links mentioned throughout the course in one convenient place.</p>',
        imgName: 'procreate701/bonuses/bonuses-6.jpg',
        bgColor: '#FFF6EE'
      },
    ],
  },
  courseCard: {
    title: 'Messy Watercolors in Procreate',
    backgroundImgName: ``,
    backgroundColor: '#65807c',
    cardImgName: `procreate301/watercolor-class-card-4.jpg`,
    desc: 'Paint vibrant and messy eye candy watercolor artwork in Procreate! Reference traditional methods while utilizing digital advantages like blend modes, selective color adjustments and layers. Follow along as each project builds in complexity with technique, process and levels of messiness. In addition to painting, you’ll learn and use multiple layers, blend modes, selections, recoloring, saturation adjustments and warping to create stunning artwork using an efficient process. Finish the course by learning how to prepare your artwork for print using a home printer or online print services to enjoy your art beyond the screen.',
    bullets: [
      'Access to 33 HD step-by-step videos',
      'Messy Watercolor Master Brush Set (208 brushes)',
      'Brush Set Extended License ($40 value)',
      'Bonus Procreate to Print module',
      'Tutorials for painting in the 3 messy levels',
      'Messy watercolor painting replication',
      'Realism and loose painting styles',
      'Unlimited updates',
      'Lifetime access to the class',
    ],
    brush: {
      enabled: true,
      image: {
        pencil: `apple2-pencil.png`,
        shadow: `sell-card-pencil-reflection-light.png`,
      },
    },
    priceColor: mainColor,
    saleColor: neonPink,
    stats: {
      modules: `#cc836f`,
      videos: `#758384`,
      hours: `#a79e8d`,
    },
  },
  faqs: [
    {
      question: 'Do you offer any refunds or guarantees?',
      answer: '<p>Yep! If you finish the course and feel you haven’t learned anything new, email me within 14 days of purchase for a full refund.</p>',
    },
    {
      question: 'How long can I access the course for?',
      answer: '<p>Forever! As long as Every Tuesday exists, this course will be available to watch. Take it at whatever pace you’d like, watch whenever, wherever and as many times as you’d like.</p>',
    },
    {
      question: 'Is this a live or pre-recorded course?',
      answer: '<p>This course is entirely pre-recorded, so you can take it whenever it works best with your schedule.</p>',
    },
    {
      question: 'Does this class come with subtitles/closed captions?',
      answer: '<p>Yes! There are English subtitles available for all videos.</p>',
    },
    {
      question: 'What will I need to take this class?',
      answer: '<p>You’ll need the Procreate app (v.5.2.9 or newer), a pressure sensitive iPad (see this list: every-tuesday.com/ipads), a stylus compatible with that iPad (I recommend the Apple Pencil) and a familiarity with Procreate (my free <a class="bold" style="color: #4D286F" target="_blank" href="https://every-tuesday.com/5x">Procreate for Beginners course</a> is recommended as a prerequisite).</p>',
    },
    {
      question: 'Do I need any special Procreate brushes to create these projects?',
      answer: '<p>Yes, and they’re all included for free with your enrollment. Refer to the course video titled ‘bonuses + install instructions’ for their location and a full installation tutorial.</p>',
    },
    {
      question: 'I’m still learning Procreate. Is this for me?',
      answer:'<p>Yep! The course was designed with beginners in mind and it increases in complexity as the course progresses. You’ll want to be familiar with the interface before you begin, though, so my free <a style="color: #4D286F" class="bold" target="_blank" href="https://every-tuesday.com/5x">Procreate for Beginners</a> course is recommended as a prerequisite.</p>'
    },
    {
      question: 'I have no experience drawing flowers. Can I still take this class?',
      answer: '<p>Yes! If you can draw irregular, imperfect shapes, then you can create every project within the course. I show you everything you need to know and offer resources to guide you if you ever get stuck along the way.</p>',
    },
    {
      question: 'If I recreate these projects following your instruction, can I sell that artwork afterward?',
      answer: '<p>You may use any of the brushes, color palettes and techniques provided in the class to create your own unique artwork to sell. You may not sell recreated class projects, as they are my own original artwork + concepts.</p>',
    },
  ],
  testimonials: [],
  letsDoThis: {
    imgName: 'procreate301/lets-do-this-2.jpg',
    altTag: 'Enroll in Messy Watercolors for Procreate today!',
    text: 'Welcome to Flat Florals in Procreate, your complete guide to painting simplified, bold and beautiful florals.',
  },
  sellCard: {
    priceColor: '#5f1212',
    saleColor: neonPink,
    cardImg: '',
    saleLink: 'https://courses.every-tuesday.com/purchase?product_id=4392057',
    link: 'https://courses.every-tuesday.com/purchase?product_id=4392058',
    paymentPlanLink: 'https://courses.every-tuesday.com/purchase?product_id=4392059',
    customBtnBackgroundColor: 'var(--ff-teal-600)',
    h2Color: `#5f1212`,
    hoverColor: {
      h2: `#fff`,
      text: 'var(--ff-teal-600)',
      overlay: `var(--ff-teal-400)`,
      cardDescription: '#fff'
    },
    bullets: [
      'Instant access to all videos and bonuses',
      'Full Bouquet Maker brush set (150+ brushes)',
      'Bonus applications module (3 projects)',
      'Private Facebook community',
      'Extended License of the brush set ($40 value)',
      'Seamless patterns with transparent backgrounds process',
      'Rose simplification tutorial',
      'Composition design tutorials',
      '14 day money back guarantee',
      'Flat style cheat sheets (pdf)',
      'All project + demo color palettes',
      'English subtitles on all videos',
      'Basic + complex style practice exercises (12 total)',
    ],
  },
}

export const gqlFragment701BrushTips = graphql`
    fragment Fragment701BrushTips on Query {
        flatExtra1:imgixImage(url: "procreate701/brushes/flat-extras/flat-extra-1-min.png") {
          gatsbyImageData(width: 100, layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:100,
              auto: "compress"
          }, srcSetMaxWidth: 100)
        }
        flatExtra2:imgixImage(url: "procreate701/brushes/flat-extras/flat-extra-2-min.png") {
            ...FragmentImgix300
        }
        flatExtra3:imgixImage(url: "procreate701/brushes/flat-extras/flat-extra-3-min.png") {
            ...FragmentImgix300
        }
        flatExtra4:imgixImage(url: "procreate701/brushes/flat-extras/flat-extra-4-min.png") {
            ...FragmentImgix300
        }
        flatExtra5:imgixImage(url: "procreate701/brushes/flat-extras/flat-extra-5-min.png") {
            ...FragmentImgix300
        }


        flatHero1:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-1-min.png") {
          ...FragmentImgix300
        }
        flatHero2:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-2-min.png") {
          ...FragmentImgix300
        }
        flatHero3:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-3-min.png") {
          ...FragmentImgix300
        }
        flatHero4:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-4-min.png") {
          ...FragmentImgix300
        }
        flatHero5:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-5-min.png") {
          ...FragmentImgix300
        }
        flatHero6:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-6-min.png") {
          ...FragmentImgix300
        }
        flatHero7:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-7-min.png") {
          ...FragmentImgix300
        }
        flatHero8:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-8-min.png") {
          ...FragmentImgix300
        }
        flatHero9:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-9-min.png") {
          ...FragmentImgix300
        }
        flatHero10:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-10-min.png") {
          ...FragmentImgix300
        }
        flatHero11:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-11-min.png") {
          ...FragmentImgix300
        }
        flatHero12:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-12-min.png") {
          ...FragmentImgix300
        }
        flatHero13:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-13-min.png") {
          ...FragmentImgix300
        }
        flatHero14:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-14-min.png") {
          ...FragmentImgix300
        }
        flatHero15:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-15-min.png") {
          ...FragmentImgix300
        }
        flatHero16:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-16-min.png") {
          ...FragmentImgix300
        }
        flatHero17:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-17-min.png") {
          ...FragmentImgix300
        }
        flatHero18:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-18-min.png") {
          ...FragmentImgix300
        }
        flatHero19:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-19-min.png") {
          ...FragmentImgix300
        }
        flatHero20:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-20-min.png") {
          ...FragmentImgix300
        }
        flatHero21:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-21-min.png") {
          ...FragmentImgix300
        }
        flatHero22:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-22-min.png") {
          ...FragmentImgix300
        }
        flatHero23:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-23-min.png") {
          ...FragmentImgix300
        }
        flatHero24:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-24-min.png") {
          ...FragmentImgix300
        }
        flatHero25:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-25-min.png") {
          ...FragmentImgix300
        }
        flatHero26:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-26-min.png") {
          ...FragmentImgix300
        }
        flatHero27:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-27-min.png") {
          ...FragmentImgix300
        }
        flatHero28:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-28-min.png") {
          ...FragmentImgix300
        }
        flatHero29:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-29-min.png") {
          ...FragmentImgix300
        }
        flatHero30:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-30-min.png") {
          ...FragmentImgix300
        }
        flatHero31:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-31-min.png") {
          ...FragmentImgix300
        }
        flatHero32:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-32-min.png") {
          ...FragmentImgix300
        }
        flatHero33:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-33-min.png") {
          ...FragmentImgix300
        }
        flatHero34:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-34-min.png") {
          ...FragmentImgix300
        }
        flatHero35:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-35-min.png") {
          ...FragmentImgix300
        }
        flatHero36:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-36-min.png") {
          ...FragmentImgix300
        }
        flatHero37:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-37-min.png") {
          ...FragmentImgix300
        }
        flatHero38:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-38-min.png") {
          ...FragmentImgix300
        }
        flatHero39:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-39-min.png") {
          ...FragmentImgix300
        }
        flatHero40:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-40-min.png") {
          ...FragmentImgix300
        }
        flatHero41:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-41-min.png") {
          ...FragmentImgix300
        }
        flatHero42:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-42-min.png") {
          ...FragmentImgix300
        }
        flatHero43:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-43-min.png") {
          ...FragmentImgix300
        }
        flatHero44:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-44-min.png") {
          ...FragmentImgix300
        }
        flatHero45:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-45-min.png") {
          ...FragmentImgix300
        }
        flatHero46:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-46-min.png") {
          ...FragmentImgix300
        }
        flatHero47:imgixImage(url: "procreate701/brushes/flat-hero/flat-hero-47-min.png") {
          ...FragmentImgix300
        }
    }
`
export const gqlFragmentProcreate701WYL = graphql`
    fragment FragmentProcreate701WYL on Query {
        wylGridImage1:imgixImage(url: "procreate701/wyl/wyl-1-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage2:imgixImage(url: "procreate701/wyl/wyl-2-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage3:imgixImage(url: "procreate701/wyl/wyl-3-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage4:imgixImage(url: "procreate701/wyl/wyl-4-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage5:imgixImage(url: "procreate701/wyl/wyl-6-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage6:imgixImage(url: "procreate701/wyl/wyl-5-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage7:imgixImage(url: "procreate701/wyl/wyl-7-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage8:imgixImage(url: "procreate701/wyl/wyl-8-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage9:imgixImage(url: "procreate701/wyl/wyl-9-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage10:imgixImage(url: "procreate701/wyl/wyl-10-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage11:imgixImage(url: "procreate701/wyl/wyl-11-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage12:imgixImage(url: "procreate701/wyl/wyl-12-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylSquareImage1:imgixImage(url: "procreate701/wyl/wyl-square-a.jpg") {
            gatsbyImageData(width:600, layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:600,
              auto: "compress"
          }, srcSetMaxWidth: 600)
        }
        wylSquareImage2:imgixImage(url: "procreate701/wyl/wyl-square-b.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 800
            })
        }
    }
`
export const gqlFragmentProcreate701AdvItems = graphql`
    fragment FragmentProcreate701AdvItems on Query {
        advItem1:imgixImage(url: "procreate701/adv-1-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
        }
        advItem2:imgixImage(url: "procreate701/adv-2-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
        }
        advItem3:imgixImage(url: "procreate701/adv-3-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
        }
        advItem4:imgixImage(url: "procreate701/adv-4-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
        }
        advItem5:imgixImage(url: "procreate701/adv-5-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
        }
        advItem6:imgixImage(url: "procreate701/adv-6-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
        }
    }
`

export default course